let ctx = null;
export function actx() {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}
export const midiToHz = (m) => 440 * Math.pow(2, (m - 69) / 12);
const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
export const noteName = (m) => `${NAMES[m % 12]}${Math.floor(m / 12) - 1}`;
export const isBlack = (m) => [1, 3, 6, 8, 10].includes(m % 12);

export function pianoNote(midi, vel = 0.8) {
  const c = actx(), t = c.currentTime, f = midiToHz(midi);
  const g = c.createGain();
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vel * 0.45, t + 0.008);
  g.gain.exponentialRampToValueAtTime(vel * 0.16, t + 0.7); g.gain.exponentialRampToValueAtTime(0.0001, t + 7);
  const lp = c.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = Math.min(9000, f * 7); lp.Q.value = 0.4;
  const oscs = [["triangle", 1, 1], ["sine", 2, 0.35], ["sine", 3, 0.1], ["sawtooth", 1.002, 0.06]].map(([type, mult, amp]) => {
    const o = c.createOscillator(); o.type = type; o.frequency.value = f * mult;
    const og = c.createGain(); og.gain.value = amp; o.connect(og).connect(lp); o.start(t); return o;
  });
  lp.connect(g).connect(c.destination);
  let done = false;
  return () => {
    if (done) return; done = true;
    const now = c.currentTime;
    g.gain.cancelScheduledValues(now); g.gain.setValueAtTime(Math.max(0.0001, g.gain.value), now); g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    oscs.forEach((o) => o.stop(now + 0.3));
  };
}

function noise(c, dur) {
  const b = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate), d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const s = c.createBufferSource(); s.buffer = b; return s;
}
let clipCurve = null;
function softClip() {
  if (!clipCurve) { clipCurve = new Float32Array(256); for (let i = 0; i < 256; i++) { const x = (i / 127.5) - 1; clipCurve[i] = Math.tanh(x * 2.2); } }
  return clipCurve;
}
export const DRUMS = ["kick", "snare", "hat", "ohat", "clap", "808", "perc"];
export function drum(kind, when, vel = 1) {
  const c = actx(), t = when ?? c.currentTime;
  const out = c.createGain(); out.gain.value = vel; out.connect(c.destination);
  const env = (g, a, d, peak = 1) => { g.gain.setValueAtTime(peak, t + a); g.gain.exponentialRampToValueAtTime(0.001, t + a + d); };
  switch (kind) {
    case "kick": { const o = c.createOscillator(), g = c.createGain(); o.frequency.setValueAtTime(170, t); o.frequency.exponentialRampToValueAtTime(44, t + 0.11); env(g, 0, 0.38); o.connect(g).connect(out); o.start(t); o.stop(t + 0.45); break; }
    case "snare": {
      const n = noise(c, 0.25), hp = c.createBiquadFilter(), g = c.createGain(); hp.type = "highpass"; hp.frequency.value = 1400; env(g, 0, 0.2, 0.8); n.connect(hp).connect(g).connect(out); n.start(t);
      const o = c.createOscillator(), g2 = c.createGain(); o.type = "triangle"; o.frequency.setValueAtTime(230, t); o.frequency.exponentialRampToValueAtTime(120, t + 0.1); env(g2, 0, 0.12, 0.6); o.connect(g2).connect(out); o.start(t); o.stop(t + 0.15); break; }
    case "hat": case "ohat": { const n = noise(c, 0.4), hp = c.createBiquadFilter(), g = c.createGain(); hp.type = "highpass"; hp.frequency.value = 7500; env(g, 0, kind === "hat" ? 0.05 : 0.28, 0.45); n.connect(hp).connect(g).connect(out); n.start(t); break; }
    case "clap": { for (const d of [0, 0.011, 0.024, 0.04]) { const n = noise(c, 0.2), bp = c.createBiquadFilter(), g = c.createGain(); bp.type = "bandpass"; bp.frequency.value = 1700; bp.Q.value = 0.9; env(g, d, d === 0.04 ? 0.18 : 0.05, 0.7); n.connect(bp).connect(g).connect(out); n.start(t + d); } break; }
    case "808": { const o = c.createOscillator(), ws = c.createWaveShaper(), g = c.createGain(); o.type = "sine"; o.frequency.setValueAtTime(58, t); o.frequency.exponentialRampToValueAtTime(36, t + 0.6); ws.curve = softClip(); env(g, 0, 0.95); o.connect(ws).connect(g).connect(out); o.start(t); o.stop(t + 1.05); break; }
    case "perc": { const o = c.createOscillator(), g = c.createGain(); o.type = "square"; o.frequency.setValueAtTime(950, t); o.frequency.exponentialRampToValueAtTime(380, t + 0.05); env(g, 0, 0.09, 0.3); o.connect(g).connect(out); o.start(t); o.stop(t + 0.12); break; }
    default:
  }
}

export function pluck(midi, vel = 0.8, when) {
  const c = actx(), t = when ?? c.currentTime, f = midiToHz(midi);
  const o = c.createOscillator(), o2 = c.createOscillator(), g2 = c.createGain(), lp = c.createBiquadFilter(), g = c.createGain();
  o.type = "sawtooth"; o.frequency.value = f; o2.type = "triangle"; o2.frequency.value = f * 2; g2.gain.value = 0.22;
  lp.type = "lowpass"; lp.frequency.setValueAtTime(Math.min(8000, f * 9), t); lp.frequency.exponentialRampToValueAtTime(Math.max(200, f * 1.4), t + 0.6); lp.Q.value = 1.4;
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vel * 0.38, t + 0.004); g.gain.exponentialRampToValueAtTime(0.0008, t + 2.4);
  o.connect(lp); o2.connect(g2).connect(lp); lp.connect(g).connect(c.destination);
  o.start(t); o2.start(t); o.stop(t + 2.5); o2.stop(t + 2.5);
}
